import { parse } from 'csv-parse/sync';
import validate from 'utils/validate';
import _ from 'lodash';

const HEADER_ALIASES = {
    latitude: 'latitude',
    lat: 'latitude',
    longitude: 'longitude',
    lng: 'longitude',
    lon: 'longitude',
    long: 'longitude',
    address: 'address',
    city: 'city',
    postalcode: 'postalCode',
    postal_code: 'postalCode',
    'postal-code': 'postalCode',
    postcode: 'postalCode',
    postal: 'postalCode',
    province: 'province',
    prov: 'province',
    region: 'province',
};

const VALID_PROVINCE_ABBREVIATIONS = [
    'AB',
    'BC',
    'MB',
    'NB',
    'NL',
    'NS',
    'NT',
    'NU',
    'ON',
    'PE',
    'QC',
    'SK',
    'YT',
    'N/A',
];

function removeBom(value = '') {
    return value.replace(/^\uFEFF/, '');
}

function normalizeHeader(value) {
    return removeBom(String(value || ''))
        .trim()
        .toLowerCase()
        .replace(/[\s_-]+/g, '');
}

function detectDelimiter(csvText) {
    const firstNonEmptyLine = String(csvText || '')
        .split(/\r?\n/)
        .find(line => line.trim().length > 0);

    if (!firstNonEmptyLine) {
        return ',';
    }

    const delimiterCandidates = [',', ';', '\t', '|'];

    const bestMatch = _.maxBy(delimiterCandidates, delimiter => {
        return firstNonEmptyLine.split(delimiter).length - 1;
    });

    const matchCount = firstNonEmptyLine.split(bestMatch).length - 1;
    return matchCount > 0 ? bestMatch : ',';
}

function normalizeHeaders(records) {
    return _.map(records, record =>
        _.reduce(
            record,
            (result, value, key) => {
                const canonicalHeader = HEADER_ALIASES[normalizeHeader(key)] || key;
                result[canonicalHeader] = value;
                return result;
            },
            {}
        )
    );
}

function parseAndNormalizeCsv(csvText) {
    const parsedRecords = parse(csvText, {
        columns: true,
        delimiter: detectDelimiter(csvText),
        trim: true,
        skip_empty_lines: true,
    });

    return normalizeHeaders(parsedRecords);
}
// Declared here because they have no use outside of this context yet
const validators = {
    latitude(value, source) {
        const isLatitudeRequired = !source.address;

        if (!isLatitudeRequired && value === '') return;

        if (!value) {
            return 'Invalid latitude - a latitude is required';
        }

        if (_.isNaN(Number(value))) {
            return 'Invalid latitude - a valid latitude is required';
        }

        const latitude = Number(value);

        if (latitude < -90 || latitude > 90) {
            return 'Invalid latitude - latitude must be between -90 and 90';
        }
    },
    longitude(value, source) {
        const isLongitudeRequired = !source.address;

        if (!isLongitudeRequired && value === '') return;

        if (!value) {
            return 'Invalid longitude - a longitude is required';
        }

        if (_.isNaN(Number(value))) {
            return 'Invalid longitude - a valid longitude is required';
        }

        const longitude = Number(value);

        if (longitude < -180 || longitude > 180) {
            return 'Invalid longitude - longitude must be between -180 and 180';
        }
    },
    address(value, source) {
        const isAddressRequired = !source.latitude || !source.longitude;

        if (isAddressRequired) {
            if (!value) {
                return 'Invalid address - an address is required';
            }
        }
    },
    city() {},
    postalCode() {},
    province(value) {
        if (!value) {
            return;
        }

        if (!_.includes(VALID_PROVINCE_ABBREVIATIONS, value)) {
            return "Invalid province - Province abbreviation must be one of: 'AB', 'BC', 'MB', 'NB', 'NL', 'NS', 'NT', 'NU', 'ON', 'PE', 'QC', 'SK', 'YT', or 'N/A'";
        }
    },
};

export function validatePois({ data }) {
    let parsedCsv;
    try {
        parsedCsv = parseAndNormalizeCsv(data);
    } catch (e) {
        console.error('Error parsing CSV: ', e);
        return ['Invalid CSV submitted with error:', e.message];
    }

    const errors = {};
    _.forEach(parsedCsv, pointData => {
        let lineErrors = validate(pointData, validators);
        const poiKey =
            pointData.poiKey || `${pointData.latitude}_${pointData.longitude}_${pointData.address}`;
        // Keep only the first validation error for each row.
        _.forEach(lineErrors, error => {
            const condensedError = error.message;
            // If this is the first error for this row, store it. Otherwise, preserve existing error.
            // Typically the first validation error encountered is the most actionable for users.
            if (!errors[poiKey]) {
                errors[poiKey] = { ...error, message: condensedError };
            }
        });
    });

    return Object.keys(errors).length ? errors : {};
}

export function validateInterpolatedPois({ data }) {
    const invalidPoints = data.filter(point => {
        // When Latitude and Longitue is equal to null or undefined or empty string. Return as invalid points
        return (
            (_.isNil(point.latitude) || point.latitude === '') &&
            (_.isNil(point.longitude) || point.longitude === '')
        );
    });

    const errors = {};
    _.forEach(invalidPoints, pointData => {
        const poiKey =
            pointData.poiKey || `${pointData.latitude}_${pointData.longitude}_${pointData.address}`;
        const condensedError = "Address couldn't be geocoded and won't be saved.";
        if (!errors[poiKey]) {
            errors[poiKey] = { field: '', value: '', message: condensedError };
        }
    });

    return Object.keys(errors).length ? errors : {};
}

export function constructPois(csvText) {
    const parsedCsv = parseAndNormalizeCsv(csvText);

    return _.map(parsedCsv, record => ({
        latitude: record.latitude,
        longitude: record.longitude,
        region: record.province,
        city: record.city,
        postalCode: record.postalCode,
        address: record.address,
        poiKey: `${record.latitude}_${record.longitude}_${record.address}`,
    }));
}
